<script setup lang="ts">
import HistoryCard from '@/components/Profile/HistoryCard.vue';
import { useFetch } from '@/composables/useFetch.js';
import { useOnboarding } from '@/composables/useOnboarding';
import { useProfileDishLists } from '@/composables/useProfileDishLists.js';
import type { MessageSchema } from '@/i18n';
import { mockDishes } from '@/onboarding/mockData';
import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore.js';
import type { DishHistoryResponse } from '@/types/api.js';
import type { Dish } from '@/types/dish.js';
import type { DishHistoryState } from '@/types/history.js';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import { nextTick, onMounted, ref, watch, watchEffect, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import LoadingView from '../LoadingView.vue';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const router = useRouter();
const toast = useToast();

const { fetchData } = useFetch();
const { isOnboardingActive } = useOnboarding();

const apiUrl = useEnvStore().apiUrl;

const searchQuery = ref('');
const isSearching = ref(false);
const dishesStore = ref(useDishesStore());

const createdUsing: Ref<string[]> = ref(['ocr', 'ia']);

const isFetchingList = ref(false);

const nextPageUrl = ref(`${apiUrl}/dishes`);
const loadedDishes = ref(0);
const totalDishes = ref(0);

const { dishes, handleDuplication, handleDeletion, handleFavoriteChange } = useProfileDishLists(toast);

function handleCardClick(id: number) {
  if (isOnboardingActive.value) {
    return;
  }
  if (props.selectMode) {
    emit('dish-clicked', dishes.value.find((dish) => dish.id === id) as Dish);
    return;
  } else {
    saveState();
    router.push(`/menu/dishes/${id}`);
  }
}

const props = defineProps<{
  selectMode?: boolean;
}>();

// When the users wants to add a dish from the planning.
const emit = defineEmits<{
  (e: 'dish-clicked', dish: Dish): void;
}>();

watchEffect(() => console.log(dishes.value.length));

async function getDishes() {
  // Pas de chargement en mode onboarding
  if (isOnboardingActive.value) {
    console.log('[Onboarding] getDishes ignoré en mode onboarding');
    return;
  }

  // This var is needed because the isFetching var from composable
  // Makes a conflict between the getDishes and the searchDishes methods
  isFetchingList.value = true;

  const { data, error } = await fetchData(nextPageUrl.value, 'GET');
  if (error) {
    console.log('error', error);
  } else {
    const response = data as DishHistoryResponse;
    console.log('response', response);
    dishes.value = [...dishes.value, ...response.data];
    loadedDishes.value = response.to;
    totalDishes.value = response.total;
    nextPageUrl.value = response.next_page_url ?? '';
  }

  isFetchingList.value = false;
}

// This method is only triggered when we type in the serach bar or when the checkboxes are clicked.
// The pagination is the handled by getDishes
async function filterDishes() {
  // Pas de filtrage/recherche en mode onboarding
  if (isOnboardingActive.value) {
    console.log('[Onboarding] filterDishes ignoré en mode onboarding');
    return;
  }

  // isSearching is needed to display search icon when the method is triggered with the watcher
  isSearching.value = true;

  // const searchUrl = `${apiUrl}/dishes/search?name=${encodeURIComponent(searchQuery.value)}`
  const searchUrl = `${apiUrl}/dishes/search?${new URLSearchParams({
    name: searchQuery.value,
    created_using: createdUsing.value.join(','),
    is_planned: 'false',
  })}`;

  const { data, error } = await fetchData(searchUrl, 'GET');

  if (error) {
    console.log('error', error);
  } else {
    const response = data as DishHistoryResponse;
    dishes.value = response.data;
    loadedDishes.value = response.to;
    totalDishes.value = response.total;
    nextPageUrl.value = response.next_page_url ?? '';
  }
  isSearching.value = false;
}

const scrollPosition = ref(0);

function saveState() {
  const stateToSave: DishHistoryState = {
    dishes: dishes.value,
    loadedDishes: loadedDishes.value,
    totalDishes: totalDishes.value,
    nextPageUrl: nextPageUrl.value,
    scrollPosition: window.scrollY,
    searchQuery: searchQuery.value,
    createdUsing: createdUsing.value,
  };
  sessionStorage.setItem('historyState', JSON.stringify(stateToSave));
}

function restoreState() {
  dishes.value = [];
  const savedState = sessionStorage.getItem('historyState');
  if (savedState) {
    const state: DishHistoryState = JSON.parse(savedState);
    dishes.value = state.dishes;
    console.log(dishes.value);
    loadedDishes.value = state.loadedDishes;
    totalDishes.value = state.totalDishes;
    nextPageUrl.value = state.nextPageUrl;
    scrollPosition.value = state.scrollPosition;
    searchQuery.value = state.searchQuery;
    createdUsing.value = state.createdUsing || []; // Provide default empty array if not found
    console.log(scrollPosition.value);
    nextTick(() => {
      window.scrollTo(0, scrollPosition.value);
    });
    sessionStorage.removeItem('historyState');
  } else {
    getDishes();
  }
}

watch(searchQuery, () => {
  if (searchQuery.value === '') {
    filterDishes();
  }
});

onMounted(() => {
  // Si l'onboarding est actif, utiliser uniquement les plats locaux
  if (isOnboardingActive.value) {
    console.log('[Onboarding] Mode actif - utilisation des plats locaux uniquement');

    // Vider la liste et utiliser uniquement les plats mock/générés
    dishes.value = [];

    // Récupérer l'ID du plat choisi pendant l'onboarding
    let generatedDishId: number | null = null;

    // 1. D'abord essayer de récupérer depuis onboarding_mock_dish_id (priorité)
    const storedDishId =
      localStorage.getItem('onboarding_mock_dish_id') ||
      sessionStorage.getItem('onboarding_mock_dish_id') ||
      localStorage.getItem('onboarding:generatedDishId') ||
      sessionStorage.getItem('onboarding:generatedDishId');

    // 2. Sinon utiliser le selectedDish du store s'il a un ID négatif
    if (storedDishId) {
      generatedDishId = parseInt(storedDishId);
    } else if (dishesStore.value.selectedDish?.id && dishesStore.value.selectedDish.id < 0) {
      generatedDishId = dishesStore.value.selectedDish.id;
    }

    if (generatedDishId && generatedDishId < 0) {
      // Trouver le plat mock correspondant
      const generatedMockDish = mockDishes.find((mockDish) => mockDish.id === generatedDishId);

      if (generatedMockDish) {
        console.log('[Onboarding] Affichage uniquement du plat généré:', generatedMockDish.name);
        dishes.value = [generatedMockDish];
        totalDishes.value = 1;
        loadedDishes.value = 1;
      }
    }

    // Pas de chargement des données API en mode onboarding
    return;
  }

  // Mode normal - logique existante
  if (dishesStore.value.shouldRefetch) {
    console.log('Should refetch !');
    sessionStorage.removeItem('historyState');
    dishesStore.value.shouldRefetch = false;
    dishes.value = [];
    getDishes();
  } else {
    console.log('restoreState');
    restoreState();
  }
});
</script>

<template>
  <main>
    <div class="mb-6 flex flex-col gap-2">
      <AutoComplete
        v-model="searchQuery"
        :suggestions="dishes"
        :loading="isSearching"
        panel-class="hidden"
        :placeholder="t('common.searchPlaceHolder')"
        @complete="filterDishes"
      />
      <div class="flex flex-row gap-6">
        <div class="flex flex-row items-center gap-2">
          <Checkbox v-model="createdUsing" input-id="ia" name="ia" value="ia" @change="filterDishes" />
          <label for="ia">{{ t('profile.dishLists.iaGenerated') }}</label>
        </div>
        <div class="flex flex-row items-center gap-2">
          <Checkbox v-model="createdUsing" input-id="ocr" name="ocr" value="ocr" @change="filterDishes" />
          <label for="ocr">{{ t('profile.dishLists.ocrGenerated') }}</label>
        </div>
      </div>
    </div>
    <div class="onboarding-history-list">
      <div v-if="dishes.length === 0 && !isFetchingList && !isSearching" class="my-8 text-center text-gray-500">
        <p class="text-sm italic">{{ t('profile.dishLists.noHistoryDishes') }}</p>
      </div>
      <HistoryCard
        v-for="dish in dishes"
        :key="dish.id"
        :dish="dish"
        :select-mode="props.selectMode"
        @card-clicked="handleCardClick(dish.id)"
        @handle-deletion="(wasDeleted: boolean) => handleDeletion(wasDeleted, dish.id)"
        @handle-duplication="(duplicationResponse: null | Dish) => handleDuplication(duplicationResponse, dish.id)"
        @handle-favorite-change="(favoriteResponse: boolean | null) => handleFavoriteChange(favoriteResponse, dish.id)"
      />
    </div>
    <Button v-if="loadedDishes < totalDishes" :loading="isFetchingList && dishes.length !== 0" :label="t('common.seeMore')" @click="getDishes()" />
    <LoadingView v-if="isFetchingList && dishes.length === 0" />
  </main>
</template>
